import type { PortalClubwiseBlock, PortalClubwiseChannel, PortalClubwiseChannelRow } from '../../types/portalReport.js';
import { LEAD_CHANNELS } from './classify.js';
import { previousMonthsForWindow } from './window.js';
import type { LeadWindow } from './window.js';

// CLUBWISE REPORT ASSEMBLY — pure. Turns the per-month, per-channel aggregates the
// detail query returns into the aggregate-only block the client sees.
//
// ⚠️⚠️ AGGREGATES ONLY. Nothing here can carry a member-level row: the input is
// already (month × channel) counts and the output is counts and percentages.
//
// ⚠️⚠️ WHOLE UPLOADED MONTHS ONLY, and `joined` is a COHORT figure: the number of
// THIS period's leads who appear in the Joiners data so far. It rises as later
// months are uploaded (somebody who enquired in June may join in August), which
// the report states beside the figure.

/** One (month × channel) aggregate from `clubwise_leads`. */
export interface ClubwiseMonthChannelRow {
  /** YYYY-MM */
  month: string;
  /** A stored channel, or 'unclassified' for a row from an upload made before channels existed. */
  channel: string;
  leads: number;
  joined: number;
}

export const CHANNEL_LABELS: Record<PortalClubwiseChannel, string> = {
  meta: 'Meta leads',
  website: 'Website leads',
  staff: 'Staff generated',
  other: 'Other',
};

export const JOINED_REPEAT_NOTE =
  'Joined counts this period’s leads who have joined so far. It rises as later months are uploaded.';

const pct1 = (n: number, d: number): number | null => (d > 0 ? Math.round((n / d) * 1000) / 10 : null);

function sumFor(rows: readonly ClubwiseMonthChannelRow[], months: ReadonlySet<string>) {
  const by = new Map<string, { leads: number; joined: number }>();
  for (const r of rows) {
    if (!months.has(r.month)) continue;
    const cur = by.get(r.channel) ?? { leads: 0, joined: 0 };
    cur.leads += r.leads;
    cur.joined += r.joined;
    by.set(r.channel, cur);
  }
  return by;
}

export function channelRowsFor(
  rows: readonly ClubwiseMonthChannelRow[],
  months: readonly string[],
): { channels: PortalClubwiseChannelRow[]; total: PortalClubwiseBlock['total']; unclassifiedLeads: number } {
  const by = sumFor(rows, new Set(months));
  const channels: PortalClubwiseChannelRow[] = LEAD_CHANNELS.map((channel) => {
    const v = by.get(channel) ?? { leads: 0, joined: 0 };
    return {
      channel,
      label: CHANNEL_LABELS[channel],
      leads: v.leads,
      joined: v.joined,
      conversionRate: pct1(v.joined, v.leads),
    };
  });
  let leads = 0;
  let joined = 0;
  for (const v of by.values()) {
    leads += v.leads;
    joined += v.joined;
  }
  const classified = channels.reduce((n, c) => n + c.leads, 0);
  return {
    channels,
    total: { leads, joined, conversionRate: pct1(joined, leads) },
    unclassifiedLeads: leads - classified,
  };
}

export function buildClubwiseBlock(input: {
  window: LeadWindow;
  rows: readonly ClubwiseMonthChannelRow[];
  mode: 'previous' | 'lastMonth' | 'lastYear';
  /** Sales over the previous months (the view's per-month sums), for the previous conversion rate. */
  previousSales: number;
}): PortalClubwiseBlock {
  const measured = input.window.measuredMonths;
  const current = channelRowsFor(input.rows, measured);
  const staff = current.channels.find((c) => c.channel === 'staff');

  // ⚠️ null while any month is part-way through: no lead comparison then.
  const previousMonths = previousMonthsForWindow(input.window, input.mode);
  let previous: PortalClubwiseBlock['previous'] = null;
  if (previousMonths) {
    const p = channelRowsFor(input.rows, previousMonths);
    const digital = p.channels
      .filter((c) => c.channel === 'meta' || c.channel === 'website')
      .reduce((n, c) => n + c.leads, 0);
    previous = {
      leads: p.total.leads,
      digitalLeads: digital,
      staffLeads: p.channels.find((c) => c.channel === 'staff')?.leads ?? 0,
      conversionRate: pct1(input.previousSales, p.total.leads),
    };
  }

  return {
    measuredMonths: [...measured],
    monthToDate: input.window.monthToDate,
    staffLeads: measured.length === 0 ? null : (staff?.leads ?? 0),
    previousMonths,
    previous,
    channels: current.channels,
    total: current.total,
    unclassifiedLeads: current.unclassifiedLeads,
  };
}

/** Per-month staff leads, for the series: null = month not uploaded (never zero). */
export function staffLeadsByMonth(
  rows: readonly ClubwiseMonthChannelRow[],
  uploadedForAll: readonly string[],
): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const m of uploadedForAll) out.set(m, 0);
  for (const r of rows) {
    if (r.channel === 'staff' && out.has(r.month)) out.set(r.month, (out.get(r.month) ?? 0) + r.leads);
  }
  return out;
}
